import type { Tab } from './nav'

/*
 * Where a notification tap lands.
 *
 * Every push this app has ever sent carried `url: '/'`, so tapping "Hayley is
 * in — 6 of 16 done" opened whichever tab happened to be showing. The
 * notification raised a question and then dropped you somewhere that did not
 * answer it.
 *
 * A pure function over the query string, so the mapping is testable without a
 * browser and the component is left with one line to run.
 *
 * UNKNOWN VALUES ARE IGNORED, NEVER GUESSED AT. A url is the one part of a
 * notification that outlives the code that wrote it - a push can sit on a lock
 * screen for two days and be tapped after a deploy - so a route that no longer
 * exists has to degrade to the default tab rather than to an error or an empty
 * screen.
 */

const TABS: Record<string, Tab> = {
  board: 'Board',
  week: 'This Wk',
  season: 'Season',
  admin: 'Admin',
  profile: 'Profile',
}

/** The Admin sub-views a notification is allowed to open. */
const ADMIN = ['board', 'players', 'recap'] as const
export type AdminTarget = (typeof ADMIN)[number]

export interface Route {
  tab: Tab
  admin?: AdminTarget
}

export function routeFromSearch(search: string): Route | null {
  let params: URLSearchParams
  try {
    params = new URLSearchParams(search)
  } catch {
    return null
  }

  const tab = TABS[params.get('tab') ?? '']
  if (!tab) return null

  /*
   * `add-game` is deliberately not reachable: it is a place you go FROM the
   * board and it carries its own way back, so a notification landing somebody
   * straight in it would strand them there.
   */
  const wanted = params.get('admin')
  const admin = ADMIN.find((a) => a === wanted)

  return tab === 'Admin' && admin ? { tab, admin } : { tab }
}
